<script setup lang="ts">
import { onMounted, computed, watch, ref } from 'vue'
import { HistoryCard } from '@/ui/common/history/card'
import { useChatHistoryPageStore } from '@/store/chat-history-page'
import { storeToRefs } from 'pinia'
import { ThreadsApi } from '@/api/modules/threads/api'
import { useQueryParams } from '@/composables/use-query-params'
import { IThreadListRequestQuery } from '@/api/modules/threads/types'
import { useThreadsStore } from '@/store/threads-store'
import { useI18n } from 'vue-i18n'
import { MessagesApi } from '@/api/modules/message'
import type { TExportFormat } from '@/api/modules/message/types'
import { openFileInNewTab } from '@/utils/file-utils'
import ExportDialog from '@/ui/common/dialogs/ExportDialog.vue'
import ConfirmDeleteDialog from '@/ui/common/dialogs/ConfirmDeleteDialog.vue'
import { TTagVariant } from '@/ui/common/tag/types'

enum ComponentStatus {
  INITIAL,
  MAIN_LOADING,
  DELETING_THREAD,
  EXPORTING_THREAD,
  RENAMING_THREAD,
}

export interface Props {
  loading?: boolean
}

const props = defineProps<Props>()

const { t, locale } = useI18n()

const { params, updateQueryParams } = useQueryParams<IThreadListRequestQuery>()

const componentStatus = ref<ComponentStatus>(ComponentStatus.INITIAL)

const threadIdAction = ref<string>('')

const currentPage = ref(1)

const pageSize = ref(8)

const chatHistoryPageStore = useChatHistoryPageStore()

const threadsStore = useThreadsStore()

const { getChatHistory, getChatCategories } = chatHistoryPageStore

const { categoriesInfoGroupedByCategory, chatHistory, countChats } =
  storeToRefs(chatHistoryPageStore)

const showExportDialog = ref(false)
const showDeleteConfirmDialog = ref(false)
const selectedThreadId = ref<string>('')
const renamingThreadId = ref<string | null>(null)
const editValue = ref<string>('')

const isLoadingMain = computed(() => componentStatus.value === ComponentStatus.MAIN_LOADING)

const isDeleting = computed(() => componentStatus.value === ComponentStatus.DELETING_THREAD)

const isExporting = computed(() => componentStatus.value === ComponentStatus.EXPORTING_THREAD)

const isRenaming = computed(() => componentStatus.value === ComponentStatus.RENAMING_THREAD)

const isAnyActionLoading = computed(() => isExporting.value || isDeleting.value || isRenaming.value)

const handleFetchAllData = async () => {
  componentStatus.value = ComponentStatus.MAIN_LOADING
  try {
    await Promise.all([
      await getChatHistory({ ...params.value, limit: pageSize.value }),
      await getChatCategories(),
    ])
  } catch (e) {
  } finally {
    componentStatus.value = ComponentStatus.INITIAL
  }
}

const handleFetchThreadList = async (params: IThreadListRequestQuery) => {
  componentStatus.value = ComponentStatus.MAIN_LOADING
  await getChatHistory({ ...params, limit: pageSize.value })
  componentStatus.value = ComponentStatus.INITIAL
}

const handleOpenExportDialog = (threadId: string) => {
  selectedThreadId.value = threadId
  showExportDialog.value = true
}

const handleOpenDeleteDialog = (threadId: string) => {
  selectedThreadId.value = threadId
  showDeleteConfirmDialog.value = true
}

const findThreadById = (threadId: string) => {
  return chatHistory.value.find((item) => item.thread_id === threadId)
}

const handleRenameThread = (threadId: string) => {
  const thread = findThreadById(threadId)
  if (!thread) return

  renamingThreadId.value = threadId
  editValue.value = thread.title
}

const handleEditInput = (value: string) => {
  editValue.value = value
}

const handleEditSave = async () => {
  if (!renamingThreadId.value) return

  const newName = editValue.value.trim()
  const thread = findThreadById(renamingThreadId.value)

  if (!newName || !thread || newName === thread.title) {
    renamingThreadId.value = null
    return
  }

  try {
    componentStatus.value = ComponentStatus.RENAMING_THREAD
    threadIdAction.value = renamingThreadId.value

    await MessagesApi.updateThread(renamingThreadId.value, newName)

    await threadsStore.handleGetThreads()

    thread.title = newName
  } catch (error) {
    console.error('Error renaming thread:', error)
  } finally {
    componentStatus.value = ComponentStatus.INITIAL
    renamingThreadId.value = null
    threadIdAction.value = ''
  }
}

const handleEditCancel = () => {
  renamingThreadId.value = null
}

const handleExportThread = async (format: TExportFormat) => {
  if (!selectedThreadId.value) return

  try {
    componentStatus.value = ComponentStatus.EXPORTING_THREAD
    threadIdAction.value = selectedThreadId.value

    const response = await MessagesApi.exportThread({
      thread_id: selectedThreadId.value,
      export_format: format,
    })

    if (response.data.download_url) {
      openFileInNewTab(response.data.download_url, response.data.filename)
    } else {
      throw new Error('No download URL received')
    }
  } catch (e) {
    console.error('Error exporting thread:', e)
  } finally {
    componentStatus.value = ComponentStatus.INITIAL
    threadIdAction.value = ''
    showExportDialog.value = false
    selectedThreadId.value = ''
  }
}

const handleDeleteThread = async () => {
  if (!selectedThreadId.value) return

  try {
    componentStatus.value = ComponentStatus.DELETING_THREAD
    threadIdAction.value = selectedThreadId.value

    await ThreadsApi.deleteThread(selectedThreadId.value)
    await threadsStore.handleGetThreads()

    await getChatHistory(params.value)
  } catch (e) {
    console.error('Error deleting thread:', e)
  } finally {
    componentStatus.value = ComponentStatus.INITIAL
    threadIdAction.value = ''
    showDeleteConfirmDialog.value = false
    selectedThreadId.value = ''
  }
}

const getThreadTitle = (threadId: string) => {
  const thread = findThreadById(threadId)
  return thread?.title || ''
}

const handleChangePage = async (page: number) => {
  updateQueryParams({
    ...params.value,
    page: page,
  })
}

watch(
  () => params.value,
  async (newValue) => {
    await handleFetchThreadList(newValue)
  },
)

watch(
  () => locale.value,
  async () => {
    await handleFetchAllData()
  },
)

onMounted(async () => {
  currentPage.value = params?.value.page || 1

  await handleFetchAllData()
})

const isShowPagination = computed(() => {
  return countChats.value / pageSize.value > 1
})

const getCardIsRenaming = (threadId: string) => {
  return renamingThreadId.value === threadId
}

const getCardIsExporting = (threadId: string) => {
  return isExporting.value && threadIdAction.value === threadId
}

const getCardIsDeleting = (threadId: string) => {
  return isDeleting.value && threadIdAction.value === threadId
}

const getCardIsDisabled = (threadId: string) => {
  return isAnyActionLoading.value && threadIdAction.value !== threadId
}
</script>

<template>
  <div v-if="componentStatus === ComponentStatus.INITIAL && chatHistory.length === 0">
    {{ t('ui.search-no-result') }}
  </div>

  <div class="AllTabsContentWrapper">
    <div v-if="isLoadingMain" class="AllTabsContentSpinner">
      <a-spin size="large" />
    </div>

    <div class="AllTabsContentGrid" v-if="!isLoadingMain">
      <HistoryCard
        v-for="({ thread_id, created_at, category, last_message, title }, i) in chatHistory"
        :key="`${i}_${thread_id}`"
        :thread_id="thread_id"
        :tagType="category as TTagVariant"
        :tagLabel="categoriesInfoGroupedByCategory[category]?.label || ''"
        :text-color="categoriesInfoGroupedByCategory[category]?.text_color || ''"
        :background-color="categoriesInfoGroupedByCategory[category]?.background_color || ''"
        :text="title"
        :text-additional="last_message || ''"
        :date="created_at"
        :when-click-export="() => handleOpenExportDialog(thread_id)"
        :when-click-delete="() => handleOpenDeleteDialog(thread_id)"
        :when-click-rename="() => handleRenameThread(thread_id)"
        :when-edit-input="handleEditInput"
        :when-edit-save="handleEditSave"
        :when-edit-cancel="handleEditCancel"
        :edit-value="editValue"
        :is-disabled="getCardIsDisabled(thread_id)"
        :is-exporting="getCardIsExporting(thread_id)"
        :is-deleting="getCardIsDeleting(thread_id)"
        :is-renaming="getCardIsRenaming(thread_id)"
      />
    </div>
  </div>

  <ExportDialog
    :is-open="showExportDialog"
    :is-loading="isExporting && threadIdAction === selectedThreadId"
    @close="
      () => {
        showExportDialog = false
        selectedThreadId = ''
      }
    "
    @export="handleExportThread"
  />

  <ConfirmDeleteDialog
    :is-open="showDeleteConfirmDialog"
    :thread-title="getThreadTitle(selectedThreadId)"
    :is-loading="isDeleting && threadIdAction === selectedThreadId"
    @close="
      () => {
        showDeleteConfirmDialog = false
        selectedThreadId = ''
      }
    "
    @confirm="handleDeleteThread"
  />

  <div class="AllTabsContentPagination">
    <a-pagination
      v-if="isShowPagination"
      v-model:current="currentPage"
      :total="countChats"
      :page-size="pageSize"
      :show-size-changer="false"
      :responsive="true"
      class="custom-pagination"
      @change="handleChangePage"
    />
  </div>
</template>

<style lang="scss" scoped>
.AllTabsContentWrapper {
  display: flex;
  flex-grow: 1;
  width: 100%;
  max-height: 636px;
}

.AllTabsContentSpinner {
  display: flex;
  align-content: center;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex-direction: column;
  flex-grow: 1;
}

.AllTabsContentGrid {
  display: grid;
  width: 100%;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  grid-gap: 8px;
  margin-bottom: 48px;
}

.AllTabsContentPagination {
  display: flex;
  align-items: flex-end;
  height: 82px;
}

@media (max-width: 767px) {
  .AllTabsContentGrid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
